/**
 * KikoBooks Environment Configuration - AZURE DEV
 * 
 * This configuration is used for Azure Dev App Service deployments.
 * It targets the development API served same-origin from https://dev.kikobooks.com
 * (the dev-kiko app service hosts both the SPA and the API).
 * 
 * Usage:
 *   - `npm run build:dev`
 *   - `ng build --configuration dev`
 * 
 * CI/CD:
 *   - GitHub Actions: uses angular.json "dev" configuration
 *   - Azure DevOps: uses "dev" build configuration
 * 
 * @environment dev
 * @version 2.0
 */
import { IEnvironment, getSubDomain } from './environment.interface';

const baseConfig: IEnvironment = {
  // ═══════════════════════════════════════════════════════════════════════════
  // CORE SETTINGS
  // ═══════════════════════════════════════════════════════════════════════════
  production: true, // Dev is a production-like build (optimized, minified)
  envName: 'dev',
  appVersion: 'v2.0',
  appThemeName: 'KikoBooks',

  // ═══════════════════════════════════════════════════════════════════════════
  // API & URL CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════
  // MONOLITH: the SPA and the API are served from the SAME app service (dev-kiko),
  // reachable at https://dev.kikobooks.com. An empty baseUrl makes every API call
  // relative to the page origin (e.g. /api/...), so requests stay SAME-ORIGIN and
  // never trigger a cross-origin CORS preflight. Do NOT point this at the
  // dev-kiko.azurewebsites.net host — that forces an unnecessary cross-origin call.
  baseUrl: '',
  appUrl: 'https://dev.kikobooks.com',
  ticketReplicaPeerAppUrl: 'https://ai.kikobooks.com',
  website: 'kikobooks.com',
  fileFetchUrl: 'https://dev.kikobooks.com/fetchUrl',
  fileUrl: 'https://kikostorageai.blob.core.windows.net/htmaster/',
  tokenUrl: null,
  loginUrl: '/auth/sign-in',
  azureSignalRBaseUrl: 'https://kikosignalr.service.signalr.net/',

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTHENTICATION & SECURITY
  // ═══════════════════════════════════════════════════════════════════════════
  userDataKey: 'authf649fc9a5f55',
  securityKey: '7061737323313233',
  reCaptchaSiteKey: '6Lfa1_AZAAAAAKcRDyRDIVJCW5sXeOPV15Pgi4KW',

  // ═══════════════════════════════════════════════════════════════════════════
  // STRIPE PAYMENT CONFIGURATION (TEST MODE)
  // ═══════════════════════════════════════════════════════════════════════════
  stripePublishableKey: 'pk_test_51QAvcVPIfD3e0CcpTFwy4iw1hvuQr0k4OenFUe7fSsH1v7ZU7OOaYFxM19IBlGbVjO6HQDLWo5eBqgv6Yv5yefYC009frlZ2e3',
  stripeInstantWalletLookup: 'test_instant_recharge',
  stripeMonthlyWalletLookup: 'test_monthly_recharge',

  // ═══════════════════════════════════════════════════════════════════════════
  // FEATURE FLAGS & TOGGLES
  // ═══════════════════════════════════════════════════════════════════════════
  isMockEnabled: false,

  // ═══════════════════════════════════════════════════════════════════════════
  // MONITORING & TELEMETRY
  // ═══════════════════════════════════════════════════════════════════════════
  appInsightsConnectionString: null, // TODO: Set Azure App Insights connection string for dev telemetry

  // ═══════════════════════════════════════════════════════════════════════════
  // SUBDOMAIN CONFIGURATION (Development)
  // ═══════════════════════════════════════════════════════════════════════════
  defaultSubDomain: 'dev',
  subDomainMappings: {
    'test': 'dev',
    'dev': 'dev',
    'dacc': 'dacc',
    'dnet': 'dnet',
    'dsmb': 'dsmb',
    'dfix': 'dfix',
    'dcam': 'dcam',
    'dimp': 'dimp',
    'dwor': 'dwor',
    'dpro': 'dpro',
    'dran': 'dran',
    'dcpo': 'dcpo'
  }
};

// Export environment with legacy property aliases for backward compatibility
export const environment = {
  ...baseConfig,

  // ═══════════════════════════════════════════════════════════════════════════
  // LEGACY PROPERTY ALIASES (for backward compatibility)
  // TODO: Migrate codebase to use new property names, then remove these
  // ═══════════════════════════════════════════════════════════════════════════
  USERDATA_KEY: baseConfig.userDataKey,
  stripe: baseConfig.stripePublishableKey,
  fileurl: baseConfig.fileUrl,
  reCaptcha_SiteKey: baseConfig.reCaptchaSiteKey,
  azureSignalRbaseUrl: baseConfig.azureSignalRBaseUrl,

  // Computed subdomain (evaluated at runtime)
  get subDomain(): string {
    return getSubDomain(baseConfig);
  }
};
